import Backbone from 'backbone';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as util from 'biojs/Util';
import localStorage from 'LocalStorage';
import {ProvidedEnzyme} from 'molbio/enzymes/EnzymeConstants';
import EnzymeConstants, {Cut, Enzyme as EnzymeSpec} from 'molbio/enzymes/EnzymeConstants';

const {enzymes}: {enzymes: Array<EnzymeSpec>} = EnzymeConstants;

export interface EnzymeAttributes {
  /** Whether the enzyme has been selected in the digest panel */
  active: boolean;
  /** The distance after the beginning of the recognition site at which the enzyme cuts. */
  cut: number;
  /** (If this is a double cutter) the distance after the beginning of the recognition site at which the enzyme makes its second cut. */
  cut2?: number;
  /** Whether the enzyme is being hovered in the active enzymes list on the digest panel */
  highlight: boolean;
  /** The enzyme's internal ID. */
  id: number;
  /** A list of enzyme IDs corresponding to the isoschizomers (recognize the same DNA sequence and make the same cut) of the enzyme. */
  isoschizomers: Array<number>;
  /** Whether the enzyme's cut sites should be visible in sequence maps */
  visible: boolean;
  /** Whether the enzyme should be visible in the enzyme catalog on the digest panel */
  catalogVisible: boolean;
  /** Optionally, a color to be used as a background color for the enzyme's cut sites in the sequence map  */
  color: null | string;
  /** Where the enzyme would cut the sequence */
  cutPositions: Array<Cut>;
  /** TODO: Fill in. */
  matchingCut: number;
  /** TODO: Fill in. */
  matchingRegion: string;
  /** The user-facing name of the enzyme. */
  name: string;
  /** The distance between the cuts on the forward and reverse strands (equivalent to length of the sticky end). */
  offset: number;
  /** TODO: Fill in. */
  providedEnzymes: {[source: string]: ProvidedEnzyme};
  /** The enzyme's recognition site. */
  seq: string;
}

export interface DoubleCutterEnzymeAttributes extends EnzymeAttributes {
  /** The distance after the beginning of the recognition site at which the enzyme makes its second cut. */
  cut2: number;
  /** The distance between the second cuts on the forward and reverse strands (equivalent to the length of the second sticky end). */
  offset2: number;
}

export interface Enzyme {
  get<K extends keyof EnzymeAttributes>(key: K): EnzymeAttributes[K];
  set(obj: Partial<EnzymeAttributes>, options?: Backbone.ModelSetOptions): Enzyme;
  set<K extends keyof EnzymeAttributes>(key: K, value: EnzymeAttributes[K]): Enzyme;
}

interface DoubleCutterEnzyme extends Enzyme {
  get<K extends keyof DoubleCutterEnzymeAttributes>(key: K): DoubleCutterEnzymeAttributes[K];
  set(obj: Partial<DoubleCutterEnzymeAttributes>, options?: Backbone.ModelSetOptions): DoubleCutterEnzyme;
  set<K extends keyof DoubleCutterEnzymeAttributes>(
    key: K,
    value: DoubleCutterEnzymeAttributes[K],
    options?: Backbone.ModelSetOptions
  ): DoubleCutterEnzyme;
}

export class Enzyme extends Backbone.Model {
  override defaults(): Partial<EnzymeAttributes> {
    return {
      active: false,
      highlight: false,
      visible: false,
      cutPositions: [],
      catalogVisible: true,
      color: null,
    };
  }

  name(): string {
    return this.get('name');
  }

  seq(): string {
    return this.get('seq');
  }

  color(): string | null {
    return this.get('color');
  }

  /** Whether the enzyme has been selected in the digest panel */
  active(): boolean {
    return this.get('active');
  }

  /** Whether the enzyme is being hovered in the active enzymes list on the digest panel */
  highlight(): boolean {
    return this.get('highlight');
  }

  /** Whether the enzyme's cut sites should be visible in sequence maps */
  visible(): boolean {
    return this.get('visible');
  }

  offset(): number {
    return this.get('offset');
  }

  recognitionSiteLength(): number {
    return this.seq().length;
  }

  postRecognitionSiteLength(): number {
    const recognitionSiteEnd = this.recognitionSiteLength();
    const stickyEndStart = Math.min(this.get('cut'), this.get('cut') + this.get('offset'));
    const length = stickyEndStart - recognitionSiteEnd;
    // If length < 0, then there is no post-recognition site.
    return Math.max(length, 0);
  }

  stickyEndLength(): number {
    return Math.abs(this.get('offset'));
  }

  doubleCutter(): this is DoubleCutterEnzyme {
    return this.get('cut2') != null;
  }

  _isVisibleHelper(type: EnzymeType): boolean {
    switch (type) {
      case EnzymeType.ALL:
        return true;
      case EnzymeType.NONE:
        return false;
      case EnzymeType.SINGLE:
        return this.get('cutPositions').length === 1;
      case EnzymeType.DOUBLE:
        return this.get('cutPositions').length === 2;
      case EnzymeType.SINGLEANDDOUBLE:
        return [1, 2].includes(this.get('cutPositions').length);
      default:
        // TODO: Remove default case when callers are converted to TS.
        assertAndContinue(false, 'Invalid type! Hiding enzyme.');
        return false; // same as EnzymeType.NONE
    }
  }

  isVisibleByCuts(type: EnzymeType, blacklist: Array<Enzyme>): boolean {
    return (
      // eslint-disable-next-line no-restricted-syntax
      this.active() || this.color() != null || (this._isVisibleHelper(type) && !_.contains(blacklist, this))
    );
  }

  updateVisibleByCuts(type: EnzymeType): void {
    this.set({visible: this._isVisibleHelper(type)});
  }

  forceVisible(): boolean {
    return this.active() || this.color() != null;
  }

  /**
   * Set both the active and visible attributes to false.
   */
  setHidden(): void {
    this.set({active: false, visible: false});
  }

  /**
   * Set the active and visible attributes to true and set the color.
   * @param color Color to set for the enzyme.
   */
  setActive(color?: string): void {
    this.set({active: true, visible: true, color});
  }
}

export enum EnzymeType {
  SINGLE = 'single',
  DOUBLE = 'double',
  SINGLEANDDOUBLE = 'singleanddouble',
  ALL = 'all',
  NONE = 'none',
}

class Enzymes extends Backbone.Collection<Enzyme> {
  static initClass(): void {
    this.prototype.model = Enzyme;
    this.prototype.comparator = 'name';
  }

  batchName(): string {
    return 'enzymes';
  }

  getEnzymesJSON(enzymeIds: Array<number>): Array<EnzymeAttributes> {
    return enzymeIds.map((id) => this.get(id).toJSON());
  }

  static getCommonEnzymes(): Enzymes {
    // eslint-disable-next-line no-restricted-syntax
    return new this(_.map(enzymes, (enzyme) => new Enzyme(util.digestableEnzyme(enzyme))));
  }

  /**
   * Mark all enzymes in the collection as inactive and hidden.
   */
  hideAll(): void {
    for (const enzyme of this.models) {
      enzyme.setHidden();
    }
  }

  /**
   * Mark all single-cutter enzymes as active and visible.
   */
  showAllSingleCutters(): void {
    for (const enzyme of this.models) {
      if (enzyme.doubleCutter()) {
        continue;
      }
      enzyme.setActive();
    }
  }

  static setLocalStorageActives(activeCollection: Enzymes): void {
    const activeIDs: {[key: string]: string} = {};
    activeCollection.each((e) => {
      activeIDs[e.id] = e.color()!;
    });
    if (_.isEmpty(activeIDs)) {
      localStorage.removeItem('active-enzymes');
    } else {
      localStorage.setItem('active-enzymes', JSON.stringify(activeIDs));
    }
  }
}

Enzymes.initClass();

export default Enzymes;
